import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { InsetModal as Modal } from '../InsetModal';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { Input } from '../design-system/Input';
import { Toggle } from '../design-system/Toggle';
import { PhoneInputComponent } from '../design-system/PhoneInput';
import { DatePickerField } from '../DatePickerField';
import { VoiceRecorder } from '../VoiceRecorder';
import { DataListRecordPicker } from './DataListRecordPicker';
import { DataListRepeaterField } from './DataListRepeaterField';
import { DataListColumn, apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

interface DataListFieldRendererProps {
  column: DataListColumn;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  businessId: string;
  listId: string;
  /** Present when editing an existing record — enables immediate upload of media files.
   * Absent on create: media files are staged as local URIs (see isLocalUri) and uploaded by
   * the parent form after the record is created and its id becomes available. */
  recordId?: string;
}

/** True for a value that is a device-local file URI staged for upload, not a persisted URL. */
export const isLocalUri = (value: any): boolean =>
  typeof value === 'string' &&
  (value.startsWith('file://') ||
    value.startsWith('content://') ||
    value.startsWith('ph://') ||
    value.startsWith('blob:'));

/** Renders the right control for a Data List column based on its type. Fully localized + RTL. */
export const DataListFieldRenderer: React.FC<DataListFieldRendererProps> = ({
  column,
  value,
  onChange,
  error,
  businessId,
  listId,
  recordId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [uploading, setUploading] = useState(false);
  const [recordPickerOpen, setRecordPickerOpen] = useState(false);
  const [relationLabel, setRelationLabel] = useState<string | null>(null);
  const [relationLabels, setRelationLabels] = useState<Record<string, string>>({});
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);

  const constraints = column.constraints || {};

  const uploadOrStage = async (uri: string, name: string, mimeType: string) => {
    // Editing an existing record: upload immediately and store the returned URL.
    if (recordId) {
      try {
        setUploading(true);
        const res = await apiService.uploadDataListRecordFile(listId, recordId, column.id, uri, name, mimeType);
        if (res.statusCode === 1000 && res.data?.url) {
          onChange(res.data.url);
        }
      } catch (err: any) {
        Alert.alert(t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.recordForm.uploadFailed'));
      } finally {
        setUploading(false);
      }
    } else {
      // Creating: stage the local URI; the form uploads it after create.
      onChange(uri);
    }
  };

  const pickImageOrVideo = async (kind: 'image' | 'video') => {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      Alert.alert(t('dataLists.field.permissionTitle'), t('dataLists.field.permissionMessage'));
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: kind === 'image' ? ImagePicker.MediaTypeOptions.Images : ImagePicker.MediaTypeOptions.Videos,
      quality: 0.8,
    });
    if (!result.canceled && result.assets && result.assets.length > 0) {
      const asset = result.assets[0];
      const name = asset.fileName || `${column.id}_${Date.now()}.${kind === 'image' ? 'jpg' : 'mp4'}`;
      const mimeType = asset.mimeType || (kind === 'image' ? 'image/jpeg' : 'video/mp4');
      await uploadOrStage(asset.uri, name, mimeType);
    }
  };

  const handleVoiceRecorded = async (uri: string) => {
    setVoiceRecorderOpen(false);
    await uploadOrStage(uri, `${column.id}_${Date.now()}.m4a`, 'audio/m4a');
  };

  const renderLabel = () => (
    <View style={[styles.labelRow, { flexDirection: rtl.rowDirection }]}>
      <Text style={[styles.label, { textAlign: rtl.textStart }]}>{column.name}</Text>
      {constraints.required && <Text style={styles.requiredStar}> *</Text>}
    </View>
  );

  const renderMediaButton = (pickLabelKey: string, onPress: () => void) => (
    <View>
      {renderLabel()}
      <TouchableOpacity
        style={[styles.mediaButton, { flexDirection: rtl.rowDirection }]}
        onPress={onPress}
        disabled={uploading}
        activeOpacity={0.7}
      >
        {uploading ? (
          <ActivityIndicator size="small" color={colors.primary} />
        ) : (
          <Ionicons name={value ? 'checkmark-circle' : 'cloud-upload-outline'} size={20} color={colors.primary} />
        )}
        <Text style={[styles.mediaButtonText, { textAlign: rtl.textStart }]} numberOfLines={1}>
          {uploading
            ? t('dataLists.field.uploading')
            : value
              ? t('dataLists.field.fileSelected')
              : t(pickLabelKey as any)}
        </Text>
        {value && !uploading && (
          <TouchableOpacity onPress={() => onChange(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Ionicons name="close-circle" size={18} color={colors.error} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
      {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
    </View>
  );

  switch (column.type) {
    case 'text': {
      // enum → segmented picker; otherwise a plain (or length-limited) text input.
      if (constraints.enum && constraints.enum.length > 0) {
        return (
          <View style={styles.field}>
            {renderLabel()}
            <View style={[styles.enumRow, { flexDirection: rtl.rowDirection }]}>
              {constraints.enum.map((option) => {
                const selected = value === option;
                return (
                  <TouchableOpacity
                    key={option}
                    style={[styles.enumOption, selected && styles.enumOptionActive]}
                    onPress={() => onChange(option)}
                  >
                    <Text style={[styles.enumOptionText, selected && styles.enumOptionTextActive]}>{option}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
          </View>
        );
      }
      return (
        <View style={styles.field}>
          <Input
            label={column.name + (constraints.required ? ' *' : '')}
            value={value != null ? String(value) : ''}
            onChangeText={onChange}
            placeholder={t('dataLists.field.textPlaceholder')}
            maxLength={typeof constraints.max === 'number' ? constraints.max : undefined}
            error={error}
          />
        </View>
      );
    }

    case 'number':
      return (
        <View style={styles.field}>
          <Input
            label={column.name + (constraints.required ? ' *' : '')}
            value={value != null ? String(value) : ''}
            onChangeText={(text) => {
              if (text.trim() === '') {
                onChange(null);
                return;
              }
              const num = Number(text);
              onChange(Number.isNaN(num) ? text : num);
            }}
            placeholder={t('dataLists.field.numberPlaceholder')}
            keyboardType="numeric"
            error={error}
          />
        </View>
      );

    case 'boolean':
      return (
        <View style={styles.field}>
          <Toggle
            label={column.name}
            value={value === true}
            onValueChange={onChange}
          />
          {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
        </View>
      );

    case 'date':
      return (
        <View style={styles.field}>
          <DatePickerField
            label={column.name + (constraints.required ? ' *' : '')}
            value={value ? new Date(value) : null}
            onChange={(date) => onChange(date ? date.toISOString() : null)}
            mode={constraints.dateMode === 'datetime' ? 'datetime' : 'date'}
          />
          {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
        </View>
      );

    case 'phone':
      return (
        <View style={styles.field}>
          <PhoneInputComponent
            label={column.name + (constraints.required ? ' *' : '')}
            value={value != null ? String(value) : ''}
            onChangeText={onChange}
            error={error}
          />
        </View>
      );

    case 'object':
      return (
        <View style={styles.field}>
          <Input
            label={column.name + (constraints.required ? ' *' : '')}
            value={typeof value === 'string' ? value : value != null ? JSON.stringify(value, null, 2) : ''}
            onChangeText={onChange}
            placeholder={t('dataLists.field.jsonPlaceholder')}
            multiline
            numberOfLines={4}
            autoCapitalize="none"
            error={error}
            inputStyle={styles.jsonInput}
          />
        </View>
      );

    case 'image':
      return <View style={styles.field}>{renderMediaButton('dataLists.field.pickImage', () => pickImageOrVideo('image'))}</View>;

    case 'video':
      return <View style={styles.field}>{renderMediaButton('dataLists.field.pickVideo', () => pickImageOrVideo('video'))}</View>;

    case 'voice':
      return (
        <View style={styles.field}>
          {renderMediaButton('dataLists.field.recordVoice', () => setVoiceRecorderOpen(true))}
          <Modal visible={voiceRecorderOpen} transparent animationType="slide" onRequestClose={() => setVoiceRecorderOpen(false)}>
            <TouchableOpacity style={styles.recorderOverlay} activeOpacity={1} onPress={() => setVoiceRecorderOpen(false)}>
              <VoiceRecorder onRecordingComplete={handleVoiceRecorded} onRecordingCancel={() => setVoiceRecorderOpen(false)} maxDuration={120} />
            </TouchableOpacity>
          </Modal>
        </View>
      );

    case 'relation': {
      if (column.relation?.multiple) {
        const ids: string[] = Array.isArray(value) ? value : [];
        return (
          <View style={styles.field}>
            {renderLabel()}
            {ids.length > 0 && (
              <View style={[styles.chipRow, { flexDirection: rtl.rowDirection }]}>
                {ids.map((id) => (
                  <View key={id} style={[styles.chip, { flexDirection: rtl.rowDirection }]}>
                    <Text style={styles.chipText} numberOfLines={1}>{relationLabels[id] || id}</Text>
                    <TouchableOpacity onPress={() => onChange(ids.filter((v) => v !== id))}>
                      <Ionicons name="close-circle" size={16} color={colors.textLight} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
            <TouchableOpacity
              style={[styles.selectButton, { flexDirection: rtl.rowDirection }]}
              onPress={() => setRecordPickerOpen(true)}
              activeOpacity={0.7}
            >
              <Text style={[styles.selectButtonText, ids.length === 0 && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]} numberOfLines={1}>
                {ids.length > 0 ? t('dataLists.field.addAnotherRecord') : t('dataLists.field.selectRecord')}
              </Text>
              <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={18} color={colors.textLight} />
            </TouchableOpacity>
            {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
            {column.relation?.targetListId && (
              <DataListRecordPicker
                visible={recordPickerOpen}
                onClose={() => setRecordPickerOpen(false)}
                targetListId={column.relation.targetListId}
                multiple
                selectedIds={ids}
                onSelect={() => {}}
                onSelectMultiple={(recordIds, labels) => {
                  onChange(recordIds);
                  setRelationLabels((prev) => ({ ...prev, ...labels }));
                }}
              />
            )}
          </View>
        );
      }
      return (
        <View style={styles.field}>
          {renderLabel()}
          <TouchableOpacity
            style={[styles.selectButton, { flexDirection: rtl.rowDirection }]}
            onPress={() => setRecordPickerOpen(true)}
            activeOpacity={0.7}
          >
            <Text style={[styles.selectButtonText, !value && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {value ? (relationLabel || String(value)) : t('dataLists.field.selectRecord')}
            </Text>
            <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={18} color={colors.textLight} />
          </TouchableOpacity>
          {error && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{error}</Text>}
          {column.relation?.targetListId && (
            <DataListRecordPicker
              visible={recordPickerOpen}
              onClose={() => setRecordPickerOpen(false)}
              targetListId={column.relation.targetListId}
              onSelect={(recordId2, label) => {
                onChange(recordId2);
                setRelationLabel(label);
              }}
            />
          )}
        </View>
      );
    }

    case 'repeater':
      return <DataListRepeaterField column={column} value={value} onChange={onChange} error={error} businessId={businessId} />;

    default:
      return null;
  }
};

const getStyles = () => StyleSheet.create({
  field: {
    marginBottom: spacing.md,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  requiredStar: {
    color: colors.error,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs / 2,
  },
  jsonInput: {
    minHeight: 96,
    textAlignVertical: 'top',
  },
  enumRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  enumOption: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  enumOptionActive: {
    borderColor: colors.primary,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  enumOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  enumOptionTextActive: {
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  mediaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
  },
  mediaButtonText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  selectButtonText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  selectButtonPlaceholder: {
    color: colors.textLight,
  },
  chipRow: {
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  chip: {
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingVertical: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
    maxWidth: '100%',
  },
  chipText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
  },
  recorderOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
});
